import React from 'react';
import Image from 'next/image';
import { getSportsbookLogoPath, resolveSportsbookDescriptor } from '@/lib/sportsbook-logo';
import { FANDUEL_SIGNUP_CTA_AMOUNT, KALSHI_SIGNUP_CTA_AMOUNT, NOVIG_SIGNUP_CTA_AMOUNT, POLYMARKET_SIGNUP_CTA_AMOUNT, PROPHETX_SIGNUP_CTA_AMOUNT, SMACKTOK_SIGNUP_CTA_AMOUNT, UNDERDOG_SIGNUP_CTA_AMOUNT, SPORTSBOOK_AFFILIATE_LINKS } from '@/lib/sportsbook-affiliate-links';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SportsbookLogoProps {
  name: string;
  size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  hideBonus?: boolean;
  disableLink?: boolean;
}

interface SportsbookLogoMarkProps {
  sportsbook: string;
  size?: number;
  className?: string;
  decorative?: boolean;
}

const sportsbookConfig: Record<string, {
  bg: string;
  text: string;
  displayName: string;
  amount?: string;
  borderColor?: string;
  glowColor?: string;
  badgeBg?: string;
  badgeBorder?: string;
  badgeText?: string;
}> = {
  PP: {
    bg: 'bg-blue-900',
    text: 'text-primary-foreground',
    displayName: 'PrizePicks',
    amount: '$100'
  },
  UD: {
    bg: 'bg-orange-600',
    text: 'text-primary-foreground',
    displayName: 'Underdog',
    amount: UNDERDOG_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#F4C542]/70',
    glowColor: 'rgba(244, 197, 66, 0.4)',
    badgeBg: 'bg-[#F4C542]/15',
    badgeBorder: 'border-[#F4C542]/70',
    badgeText: 'text-[#F4C542]'
  },
  SL: {
    bg: 'bg-purple-600',
    text: 'text-primary-foreground',
    displayName: 'Sleeper',
    amount: '$100',
    borderColor: 'border-purple-400/70',
    glowColor: 'rgba(192, 132, 252, 0.4)',
    badgeBg: 'bg-purple-500/15',
    badgeBorder: 'border-purple-400/70',
    badgeText: 'text-purple-200'
  },
  DK: {
    bg: 'bg-green-600',
    text: 'text-primary-foreground',
    displayName: 'DraftKings',
    amount: '$250'
  },
  FD: {
    bg: 'bg-blue-600',
    text: 'text-primary-foreground',
    displayName: 'FanDuel',
    amount: FANDUEL_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#38bdf8]/70',
    glowColor: 'rgba(56, 189, 248, 0.4)',
    badgeBg: 'bg-[#38bdf8]/15',
    badgeBorder: 'border-[#38bdf8]/70',
    badgeText: 'text-[#38bdf8]'
  },
  CS: {
    bg: 'bg-yellow-600',
    text: 'text-primary',
    displayName: 'Caesars',
    amount: '$1500'
  },
  MG: {
    bg: 'bg-[#c1a96c]',
    text: 'text-slate-900',
    displayName: 'BetMGM',
    amount: '$5'
  },
  FA: {
    bg: 'bg-primary',
    text: 'text-primary-foreground',
    displayName: 'Fanatics'
  },
  HR: {
    bg: 'bg-[#7c5df1]',
    text: 'text-primary-foreground',
    displayName: 'Hard Rock'
  },
  BR: {
    bg: 'bg-[#7AFFFF]',
    text: 'text-slate-900',
    displayName: 'Betr',
    amount: '$200',
    borderColor: 'border-[#7AFFFF]/70',
    glowColor: 'rgba(122, 255, 255, 0.4)',
    badgeBg: 'bg-[#7AFFFF]/15',
    badgeBorder: 'border-[#7AFFFF]/70',
    badgeText: 'text-[#7AFFFF]'
  },
  NV: {
    bg: 'bg-[#79cfff]',
    text: 'text-primary-foreground',
    displayName: 'Novig',
    amount: NOVIG_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#79cfff]/70',
    glowColor: 'rgba(121, 207, 255, 0.4)',
    badgeBg: 'bg-[#79cfff]/15',
    badgeBorder: 'border-[#79cfff]/70',
    badgeText: 'text-[#79cfff]'
  },
  PX: {
    bg: 'bg-[#0CA249]',
    text: 'text-primary-foreground',
    displayName: 'ProphetX',
    amount: PROPHETX_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#0CA249]/70',
    glowColor: 'rgba(12, 162, 73, 0.4)',
    badgeBg: 'bg-[#0CA249]/15',
    badgeBorder: 'border-[#0CA249]/70',
    badgeText: 'text-[#0CA249]'
  },
  DB: {
    bg: 'bg-[#7742FF]',
    text: 'text-primary-foreground',
    displayName: 'Dabble',
    amount: '$10',
    borderColor: 'border-[#7742FF]/70',
    glowColor: 'rgba(119, 66, 255, 0.4)',
    badgeBg: 'bg-[#7742FF]/15',
    badgeBorder: 'border-[#7742FF]/70',
    badgeText: 'text-[#7742FF]'
  },
  ParlayPlay: {
    bg: 'bg-[#f7c531]',
    text: 'text-gray-900',
    displayName: 'ParlayPlay',
    amount: '$100',
    borderColor: 'border-[#f7c531]/70',
    glowColor: 'rgba(247, 197, 49, 0.4)',
    badgeBg: 'bg-[#f7c531]/15',
    badgeBorder: 'border-[#f7c531]/70',
    badgeText: 'text-[#f7c531]'
  },
  CR: {
    bg: 'bg-[#263387]',
    text: 'text-slate-100',
    displayName: 'Circa',
    borderColor: 'border-[#263387]/70',
    glowColor: 'rgba(38, 51, 135, 0.4)',
    badgeBg: 'bg-[#263387]/15',
    badgeBorder: 'border-[#263387]/70',
    badgeText: 'text-[#263387]'
  },
  PN: {
    bg: 'bg-[#0b1624]',
    text: 'text-white',
    displayName: 'Pinnacle',
    borderColor: 'border-[#ff6600]/70',
    glowColor: 'rgba(255, 102, 0, 0.4)',
    badgeBg: 'bg-[#ff6600]/15',
    badgeBorder: 'border-[#ff6600]/70',
    badgeText: 'text-[#ff6600]'
  },
  PM: {
    bg: 'bg-[#2e5cff]',
    text: 'text-primary-foreground',
    displayName: 'Polymarket',
    amount: POLYMARKET_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#2e5cff]/70',
    glowColor: 'rgba(46, 92, 255, 0.4)',
    badgeBg: 'bg-[#2e5cff]/15',
    badgeBorder: 'border-[#2e5cff]/70',
    badgeText: 'text-[#2e5cff]'
  },
  kalshi: {
    bg: 'bg-[#73d9b3]',
    text: 'text-slate-900',
    displayName: 'Kalshi',
    amount: KALSHI_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#73d9b3]/70',
    glowColor: 'rgba(115, 217, 179, 0.4)',
    badgeBg: 'bg-[#73d9b3]/15',
    badgeBorder: 'border-[#73d9b3]/70',
    badgeText: 'text-[#73d9b3]'
  },
  RB: {
    bg: 'bg-[#FF914D]',
    text: 'text-primary-foreground',
    displayName: 'Rebet',
    amount: '$100',
    borderColor: 'border-[#FF914D]/70',
    glowColor: 'rgba(255, 145, 77, 0.4)',
    badgeBg: 'bg-[#FF914D]/15',
    badgeBorder: 'border-[#FF914D]/70',
    badgeText: 'text-[#FF914D]'
  },
  DKP: {
    bg: 'bg-[#1eb954]',
    text: 'text-white',
    displayName: 'DK Predictions'
  },
  KM: {
    bg: 'bg-[#F05A28]',
    text: 'text-white',
    displayName: 'Kambi'
  },
  BO: {
    bg: 'bg-[#ff0000]',
    text: 'text-white',
    displayName: 'BetOnline'
  },
  PRB: {
    bg: 'bg-[#e85d04]',
    text: 'text-white',
    displayName: 'PropBuilder'
  },
  BV: {
    bg: 'bg-[#cc0000]',
    text: 'text-white',
    displayName: 'Bovada'
  },
  BRS: {
    bg: 'bg-[#7AFFFF]',
    text: 'text-slate-900',
    displayName: 'Betr Sportsbook'
  },
  SH: {
    bg: 'bg-[#7717FF]',
    text: 'text-white',
    displayName: 'Shuffle'
  },
  SZ: {
    bg: 'bg-[#EF330E]',
    text: 'text-white',
    displayName: 'Sportzino'
  },
  SBAG: {
    bg: 'bg-[#FF8C00]',
    text: 'text-white',
    displayName: 'SportsBetting AG'
  },
  DKP6: {
    bg: 'bg-[#22c55e]',
    text: 'text-white',
    displayName: 'DK Pick6'
  },
  TG: {
    bg: 'bg-[#1B4D3E]',
    text: 'text-white',
    displayName: 'Tailgate'
  },
  SLP: {
    bg: 'bg-[#c084fc]',
    text: 'text-slate-900',
    displayName: 'Sleeper Markets'
  },
  CT: {
    bg: 'bg-white',
    text: 'text-[#FF4A00]',
    displayName: 'Courtside',
    amount: '$100',
    borderColor: 'border-[#FF4A00]/70',
    glowColor: 'rgba(255, 74, 0, 0.4)',
    badgeBg: 'bg-[#FF4A00]/15',
    badgeBorder: 'border-[#FF4A00]/70',
    badgeText: 'text-[#FF4A00]'
  },
  BM: {
    bg: 'bg-[#7DD3FC]',
    text: 'text-slate-900',
    displayName: 'Boom'
  },
  CB: {
    bg: 'bg-white',
    text: 'text-[#8B8FA3]',
    displayName: 'Chalkboard Picks',
    amount: '$100',
    borderColor: 'border-[#8B8FA3]/70',
    glowColor: 'rgba(139, 143, 163, 0.4)',
    badgeBg: 'bg-[#8B8FA3]/15',
    badgeBorder: 'border-[#8B8FA3]/70',
    badgeText: 'text-[#8B8FA3]'
  },
  CBS: {
    bg: 'bg-white',
    text: 'text-[#8B8FA3]',
    displayName: 'Chalkboard',
    amount: '$100',
    borderColor: 'border-[#8B8FA3]/70',
    glowColor: 'rgba(139, 143, 163, 0.4)',
    badgeBg: 'bg-[#8B8FA3]/15',
    badgeBorder: 'border-[#8B8FA3]/70',
    badgeText: 'text-[#8B8FA3]'
  },
  SMK: {
    bg: 'bg-black',
    text: 'text-[#17F6A5]',
    displayName: 'SmackTok',
    amount: SMACKTOK_SIGNUP_CTA_AMOUNT,
    borderColor: 'border-[#17F6A5]/70',
    glowColor: 'rgba(23, 246, 165, 0.4)',
    badgeBg: 'bg-[#17F6A5]/15',
    badgeBorder: 'border-[#17F6A5]/70',
    badgeText: 'text-[#17F6A5]'
  },
  '4CX': {
    bg: 'bg-[#343B86]',
    text: 'text-white',
    displayName: '4Cx'
  },
  '4C': {
    bg: 'bg-[#343B86]',
    text: 'text-white',
    displayName: '4casters'
  },
  SX: {
    bg: 'bg-[#40c0a0]',
    text: 'text-white',
    displayName: 'SX Bet'
  },
  B105: {
    bg: 'bg-[#3083dc]',
    text: 'text-white',
    displayName: 'Bet105'
  },
  ONYX: {
    bg: 'bg-[#8B5CF6]',
    text: 'text-white',
    displayName: 'Onyx Odds'
  },
  betonline: {
    bg: 'bg-[#ff0000]',
    text: 'text-white',
    displayName: 'BetOnline'
  },
  propbuilder: {
    bg: 'bg-[#e85d04]',
    text: 'text-white',
    displayName: 'PropBuilder'
  },
  bovada: {
    bg: 'bg-[#cc0000]',
    text: 'text-white',
    displayName: 'Bovada'
  }
};

const sizeClasses = {
  xxs: 'w-[22px] h-[22px]',
  xs: 'w-[32px] h-[32px]',
  sm: 'w-[38px] h-[38px]',
  md: 'w-[54px] h-[54px]',
  lg: 'w-[64px] h-[64px]'
};

const imageSizes = {
  xxs: 22,
  xs: 32,
  sm: 38,
  md: 54,
  lg: 64
};

export const SportsbookLogoMark: React.FC<SportsbookLogoMarkProps> = ({
  sportsbook,
  size = 18,
  className,
  decorative = false,
}) => {
  const descriptor = resolveSportsbookDescriptor(sportsbook);
  const altText = decorative ? '' : `${descriptor?.display ?? sportsbook} logo`;
  const imagePath = getSportsbookLogoPath(sportsbook, { fallback: 'default' });
  const wrapperClassName = className
    ? `inline-flex items-center justify-center shrink-0 rounded-md overflow-hidden ${className}`
    : 'inline-flex items-center justify-center shrink-0 rounded-md overflow-hidden';

  return (
    <span
      className={wrapperClassName}
      style={{ width: size, height: size }}
      aria-hidden={decorative || undefined}
    >
      <Image
        src={imagePath}
        alt={altText}
        width={size}
        height={size}
        className="object-contain"
        style={{ width: size, height: size }}
        unoptimized={true}
      />
    </span>
  );
};

export const SportsbookLogo: React.FC<SportsbookLogoProps> = ({
  name,
  size = 'md',
  showLabel = false,
  hideBonus = false,
  disableLink = false
}) => {
  const config = sportsbookConfig[name] || {
    bg: 'bg-muted',
    text: 'text-foreground',
    displayName: name
  };

  const linkKeyCandidates = [name.toLowerCase(), config.displayName.toLowerCase(), resolveSportsbookDescriptor(name)?.id ?? ''];
  const href = disableLink ? undefined : linkKeyCandidates.reduce<string | undefined>((acc, key) => {
    if (acc) {
      return acc;
    }
    return SPORTSBOOK_AFFILIATE_LINKS[key];
  }, undefined);

  const sizeClass = sizeClasses[size];
  const imageDimension = imageSizes[size];

  const imagePath = getSportsbookLogoPath(name);
  const hasSignupBonus = !hideBonus && config.amount && config.borderColor;

  const borderClass = hasSignupBonus && config.borderColor
    ? `border-2 ${config.borderColor}`
    : 'border border-border';

  const glowStyle = hasSignupBonus && config.glowColor
    ? `0 0 12px ${config.glowColor}, 0 1px 2px rgba(0, 0, 0, 0.1)`
    : '0 1px 2px rgba(0, 0, 0, 0.1)';

  const logoContent = (
    <div className={`flex flex-col items-center ${hasSignupBonus ? 'sm:pb-1 sm:pr-1' : ''}`}>
      <div className="relative">
        <div
          className={`${sizeClass} rounded-lg flex items-center justify-center overflow-hidden relative bg-white/[0.06] ${borderClass} ${hasSignupBonus ? 'animate-pulse-subtle' : ''}`}
          style={{
            boxShadow: glowStyle
          }}
        >
          <div className={`absolute inset-0 ${config.bg} ${config.text} flex items-center justify-center font-bold text-xs`}>
            {name}
          </div>

          <Image
            src={imagePath}
            alt={`${config.displayName} logo`}
            width={imageDimension}
            height={imageDimension}
            className="z-10 object-contain"
            priority={true}
            unoptimized={true}
          />
        </div>

        {hasSignupBonus && (
          <span
            className={`max-sm:hidden absolute -bottom-1 -right-1 z-20 inline-flex items-center rounded-full border px-1 text-[9px] leading-[13px] font-bold tracking-wide whitespace-nowrap ${
              config.badgeBorder || 'border-emerald-400/70'
            } ${
              config.badgeText || 'text-emerald-200'
            }`}
            style={{
              backgroundColor: 'var(--color-surface-elevated)',
              boxShadow: '0 0 0 2px var(--color-surface-chrome)'
            }}
          >
            {config.amount}
          </span>
        )}
      </div>
    </div>
  );

  const interactiveLogo = href ? (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          className="focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:ring-primary rounded-md cursor-pointer"
          aria-label={`Open ${config.displayName} offer in a new tab`}
        >
          {logoContent}
        </a>
      </TooltipTrigger>
      {config.amount && (
        <TooltipContent>
          Sign up for a bonus {config.amount}
        </TooltipContent>
      )}
    </Tooltip>
  ) : (
    logoContent
  );

  const content = (
    <div className="flex flex-col items-center">
      {interactiveLogo}

      {showLabel && (
        <div className="mt-1 text-xs text-muted-foreground">
          {config.displayName}
          {config.amount && <div className="text-success">{config.amount}</div>}
        </div>
      )}
    </div>
  );

  return (
    <TooltipProvider>
      {content}
    </TooltipProvider>
  );
};
